<template>
    <label v-if="!$attrs.id" :for="getId" :class="{'sr-only' : !showLabel}" class="block text-sm font-medium leading-5 pb-1.5 text-bhav-blue-500">{{ label }}</label>
    <ul v-if="float" :ref="getId" class="bg-bhav-cool-gray-50 rounded-md cursor-pointer shadow-sm block group sm:text-sm sm:leading-5 w-auto max-w-[22.5rem] md:max-w-96 lg:max-w-none text-bhav-blue-500" :class="[(open === id ? 'z-20' : 'z-0'), $attrs.class ?? '']" @click.stop="toggleDropdown">
        <li aria-hidden="true" class="relative transition-all duration-150 ease-in-out" :class="[(open === id ? 'z-20' : 'z-0')]">
            <p class="p-3 pr-8 whitespace-nowrap text-ellipsis w-full overflow-hidden relative bg-bhav-cool-gray-50 rounded-md z-10">
                {{ selectedLabel }}
                <span v-if="open !== id" aria-hidden="true" class="z-20 absolute top-0 right-0 bottom-0 flex items-center justify-end p-2"><span class="fak fa-arrow-down"></span></span>
            </p>
            <ul id="regular" class="z-10 transition-all ease-in-out duration-50 rounded-md overflow-hidden overflow-y-auto max-h-96 absolute flex flex-col" :class="[options.length > 0 && open === id ? 'min-w-full max-w-screen opacity-100 shadow-xl bg-bhav-cool-gray-50' : 'max-w-0 opacity-0 bg-transparent', (collision.x > collision.mx ? 'right-0' : 'left-0'), (collision.y > collision.my ? 'bottom-0' : 'top-0')]">
                <li v-if="allowNull" class="flex items-center hover:bg-bhav-blue-200 px-2 py-1 whitespace-nowrap cursor-pointer order-0" @click.stop="selectOption(null)">
                    <span class="p-2 pr-5" :class="value === null ? 'fas fa-circle-check text-green' : 'w-[30px] aspect-square inline-block'"></span>{{ empty }}
                </li>
                <li v-for="option,i in options" :key="`option-${i}`" class="flex items-center hover:bg-bhav-blue-200 px-2 py-1 whitespace-nowrap cursor-pointer order-0" :class="value === option.value ? '' : ''" @click.stop="selectOption(option.value)">
                    <span class="p-2 pr-5" :class="value === option.value ? 'fas fa-circle-check text-green' : 'w-[30px] aspect-square inline-block'"></span>{{ option.label }}
                </li>
            </ul>
            
        </li>
    </ul>
    <ul v-else :ref="getId" class="bg-bhav-cool-gray-50 rounded-md cursor-pointer shadow-sm block group sm:text-sm sm:leading-5 w-auto max-w-[22.5rem] md:max-w-96 lg:max-w-full text-bhav-blue-500" :class="[(open === id ? 'z-20' : 'z-0'), $attrs.class ?? '']" @click.stop="toggleDropdown">
        <li aria-hidden="true" class="relative transition-all duration-150 ease-in-out" :class="[(options.length > 0 && open === id ? 'z-20 shadow-xl' : 'z-0')]">
            <p class="p-3 pr-8 whitespace-nowrap text-ellipsis w-full overflow-hidden z-10 relative">
                {{ selectedLabel }}
                <span aria-hidden="true" class="block z-20 absolute top-0 right-0 bottom-0 items-center justify-end p-2"><span class="fak fa-arrow-down"></span></span>
            </p>
            <ul id="floating" class="bg-transparent z-10 bg-bhav-cool-gray-50 transition-all ease-in-out duration-50 rounded-b-md overflow-hidden overflow-y-auto max-h-96 flex flex-col" :class="[options.length > 0 && open === id ? 'w-full h-full opacity-100' : 'h-0 w-0 opacity-0']" @click.stop>
                <li v-if="allowNull" class="flex items-center hover:bg-bhav-blue-200 px-2 py-1 whitespace-nowrap cursor-pointer w-full" @click.stop="selectOption(null)">
                    <span class="p-2" :class="value === null ? 'fas fa-circle-check text-green' : 'w-[30px] aspect-square inline-block'"></span>{{ empty }}
                </li>
                <li v-for="option,i in options" :key="`option-${i}`" class="flex items-center gap-2 hover:bg-bhav-blue-200 px-2 py-1 cursor-pointer" :class="value === option.value ? '' : ''" @click.stop="selectOption(option.value)">
                    <span class="w-5 h-5 shrink-0 flex items-center justify-center" :class="value === option.value ? 'fas fa-circle-check text-green' : ''"></span>
                    <span class="whitespace-nowrap overflow-x-hidden text-ellipsis">{{ option.label }}</span>
                </li>
            </ul>
            
        </li>
    </ul>
</template>

<script>
import { useFormsStore } from '@/stores/forms'
import { useId } from 'vue';

export default {
    setup() {
        return {
            id: useId()
        }
    },
    data() {
        return {
            collision: {
                x: 0,
                y: 0,
                mx: .6,
                my: .6

            }
        }
    },
    emits: [
        'update:modelValue'
    ],
    props: {
        modelValue: {
            required: true
        },
        showLabel: {
            type: Boolean,
            default: true
        },
        label: {
            type: String,
        },
        focus: {
            type: Boolean,
            default: false
        },
        options: {
            type: Array,
            default: () => []
        },
        newCollisionX: {
            type: [Boolean, Number],
            default: false
        },
        newCollisionY: {
            type: [Boolean, Number],
            default: false
        },
        allowNull: {
            type: Boolean,
            default: true
        },
        empty: {
            type: String,
            default: "None Selected..."
        },
        float: {
            type: Boolean,
            default: true
        }
    },
    computed: {
        open: {
            get() {
                return useFormsStore().open;
            },
            set(open) {
                useFormsStore().setOpen(open)
            }
        },
        value: {
            get() {
                return this.modelValue
            },
            set(value) {
                this.$emit('update:modelValue', value)
            }
        },
        selectedLabel() {
            if (this.value === null || this.value === undefined || this.value === '') {
                return this.empty;
            }
            const selected = this.options.find(option => option.value === this.value);
            return selected ? selected.label : this.empty;
        },
        getId() {
            return this.$attrs.id || `field-${this.$.uid}`;
        }
    },
    methods: {
        toggleDropdown(e) {
            this.avoidCollisions(e);
            this.open = this.open === this.id ? null : this.id;
        },
        selectOption(value) {
            this.value = value;
            this.closeDropdown();
        },
        closeDropdown() {
            this.open = null;
        },
        handleClickOutside(event) {
            if (this.open === this.id && this.$refs[this.getId] && !this.$refs[this.getId].contains(event.target)) {
                this.closeDropdown();
            }
        },
        avoidCollisions(e) {
            if(this.open !== null) return;
            this.collision.x = (e.x - e.target.offsetLeft) / window.innerWidth;
            this.collision.y = (e.y - e.target.offsetTop) / window.innerHeight;
        }
    },
    mounted() {
        this.$nextTick(() => {
            document.addEventListener('click', this.handleClickOutside);
        });
        if(this.newCollisionX) {
            this.collision.mx = this.newCollisionX
        }

        if(this.newCollisionY) {
            this.collision.my = this.newCollisionY
        }
        
        if(this.focus) {
            this.$refs[this.getId].focus();
        }
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleClickOutside);
    }
}
</script>